import {
  AllergyIntolerance,
  Coding,
  Condition,
  DiagnosticReport,
  FamilyMemberHistory,
  Immunization,
  Observation,
  Procedure,
} from "@medplum/fhirtypes";
import { Smart } from "@metriport/fhir-sdk";
import { createPracticeFusionCodeableConcept } from "@metriport/shared/external";
import { EhrWritePath } from "../../../../domain/ehr";
import { getValueAndInterpretation } from "../../shared-components/fhir-sections/labs/shared";
import { MedicationWithRefs } from "../../shared-components/fhir-sections/medications/table-data";
import { CurrentEhr } from "../../shared-logic/ehr-context/ehr-context";
import {
  isAllergyIntolerance,
  isCondition,
  isConditionSeries,
  isDiagnosticReport,
  isFamilyMemberHistory,
  isImmunization,
  isImmunizationSeries,
  isMedicationSeries,
  isObservation,
  isObservationSeries,
  isProcedure,
  isProcedureSeries,
} from "./type-guards";

/**
 * EHR WRITE-BACK VALIDATOR (Logic copied from V1)
 *
 * Centralized validation logic for determining if a resource can be written to an EHR.
 * Each EHR has specific requirements (code allowlists, required fields, etc.)
 */

// Code system constants (copied from V1 shared.ts)
const LOINC_CODE = "loinc";
const CPT_CODE = "cpt";
const CVX_CODE = "cvx";
const RX_NORM_CODE = "rxnorm";
const SNOMED_CODE = "snomed";
const ICD_10_CODE = "icd-10";

const UNKNOWN_DISPLAY = "Unknown";
const UNK_CODE = "UNK";

type CodeMap = {
  system: string;
  code: string;
  display: string;
};

/**
 * Helper to find the first coding that matches any of the specified systems
 * Uses substring matching (e.g., "loinc" matches "http://loinc.org")
 * COPIED FROM V1 shared.ts
 */
function getFirstCodeSpecified(
  coding: Coding[] | undefined,
  systemsList: string[]
): CodeMap | undefined {
  let specifiedCode: CodeMap | undefined = undefined;

  if (systemsList.length && coding) {
    for (const system of systemsList) {
      let matchedCoding: Coding | undefined;
      for (const codingEntry of coding) {
        const lowerCaseSystem = codingEntry.system?.toLowerCase();
        if (lowerCaseSystem?.includes(system) && codingEntry.code) {
          matchedCoding = codingEntry;
          break;
        }
      }

      if (matchedCoding) {
        specifiedCode = {
          system: matchedCoding.system ?? UNK_CODE,
          code: matchedCoding.code ?? UNKNOWN_DISPLAY,
          display: matchedCoding.display ?? UNKNOWN_DISPLAY,
        };
        break;
      }
    }
  }

  return specifiedCode;
}

// Athena vital LOINC codes allowlist
const VITAL_CODES_ATHENA = [
  "8310-5", // Body temperature
  "8867-4", // Heart rate
  "9279-1", // Respiratory rate
  "2708-6", // Oxygen saturation
  "8462-4", // Diastolic BP
  "8480-6", // Systolic BP
  "29463-7", // Body weight
  "8302-2", // Body height
  "39156-5", // BMI
];

// Canvas vital LOINC codes allowlist
const VITAL_CODES_CANVAS = [
  "85354-9", // Blood pressure panel
  "29463-7", // Body weight
  "8302-2", // Body height
  "8867-4", // Heart rate
  "8310-5", // Body temperature
  "2708-6", // Oxygen saturation
  "59408-5", // Oxygen saturation in Arterial blood by Pulse oximetry
  "9279-1", // Respiratory rate
  "56086-2", // Body Mass Index
];

type ValidationResult = {
  isValid: boolean;
  reason?: string;
};

// Generic user-friendly messages
const GENERIC_ERROR_MESSAGE = "This item cannot be written to your EHR.";
const IS_VALID = { isValid: true };
const GENERIC_IS_INVALID = { isValid: false, reason: GENERIC_ERROR_MESSAGE };

/**
 * Main validator function - routes to specific validators based on path and EHR
 */
export function validateEhrWriteBack(
  originalData: unknown,
  path: EhrWritePath,
  currentEhr: CurrentEhr
): ValidationResult {
  switch (path) {
    case "vitals":
      return validateVital(originalData, currentEhr);
    case "lab":
      return validateLab(originalData);
    case "condition":
      return validateCondition(originalData, currentEhr);
    case "immunization":
      return validateImmunization(originalData, currentEhr);
    case "medication":
      return validateMedication(originalData, currentEhr);
    case "procedure":
      return validateProcedure(originalData);
    case "allergy":
      return validateAllergy(originalData, currentEhr);
    case "note":
      return validateNote(originalData);
    case "family-history":
      return validateFamilyHistory(originalData);
    default:
      return { isValid: false, reason: GENERIC_ERROR_MESSAGE };
  }
}

// ============================================================================
// VITALS VALIDATION
// ============================================================================

function validateVital(data: unknown, currentEhr: CurrentEhr): ValidationResult {
  const observation = extractObservation(data);
  if (!observation) return GENERIC_IS_INVALID;

  // Get LOINC code using V1 logic
  const loincCoding = getFirstCodeSpecified(observation.code?.coding ?? [], [LOINC_CODE]);
  if (!loincCoding) return GENERIC_IS_INVALID;

  // EHR-specific validation (matches V1 exactly)
  if (currentEhr.isAthena) {
    if (!VITAL_CODES_ATHENA.includes(loincCoding.code)) return GENERIC_IS_INVALID;
  } else if (currentEhr.isCanvas) {
    if (!loincCoding.code) return GENERIC_IS_INVALID;
    if (!loincCoding.display) return GENERIC_IS_INVALID;
    if (!VITAL_CODES_CANVAS.includes(loincCoding.code)) return GENERIC_IS_INVALID;
    if (!observation.status) return GENERIC_IS_INVALID;
  }

  // Check for units (matches V1 exactly)
  const normalizedUnit = observation.valueQuantity?.unit?.replace(/[{()}]/g, "");
  const units = normalizedUnit?.toLowerCase();
  if (!units) return GENERIC_IS_INVALID;

  return IS_VALID;
}

// ============================================================================
// LAB VALIDATION
// ============================================================================

function validateLab(data: unknown): ValidationResult {
  const lab = extractObservation(data);
  if (!lab) return GENERIC_IS_INVALID;

  // V1 lab validation logic
  const loincCoding = getFirstCodeSpecified(lab.code?.coding ?? [], [LOINC_CODE]);
  if (!loincCoding) return GENERIC_IS_INVALID;

  // Use shared function for value extraction (includes blacklist filtering)
  const { value, unit, referenceRange } = getValueAndInterpretation(lab);

  if (!unit) return GENERIC_IS_INVALID;

  // Check for value - properly handle zero values (don't reject 0)
  if (value === undefined || value === null || value === "") return GENERIC_IS_INVALID;

  if (!referenceRange) return GENERIC_IS_INVALID;

  const resultStatus = lab.status;
  if (!resultStatus) return GENERIC_IS_INVALID;

  const date = lab.effectiveDateTime || lab.effectivePeriod?.start;
  if (!date) return GENERIC_IS_INVALID;

  return IS_VALID;
}

// ============================================================================
// CONDITION VALIDATION
// ============================================================================

function validateCondition(data: unknown, currentEhr: CurrentEhr): ValidationResult {
  const condition = extractCondition(data);
  if (!condition) return GENERIC_IS_INVALID;

  const icd10Coding = getFirstCodeSpecified(condition.code?.coding ?? [], [ICD_10_CODE]);
  const snomedCoding = getFirstCodeSpecified(condition.code?.coding ?? [], [SNOMED_CODE]);
  const startDate = condition.onsetDateTime ?? condition.onsetPeriod?.start;
  const status = condition.clinicalStatus?.coding?.[0]?.code;

  if (currentEhr.isHealthie) {
    if (!icd10Coding || !icd10Coding.code) return GENERIC_IS_INVALID;
  } else if (currentEhr.isElation) {
    if (!snomedCoding) return GENERIC_IS_INVALID;
    if (!startDate) return GENERIC_IS_INVALID;
    if (!status) return GENERIC_IS_INVALID;
  } else if (currentEhr.isCanvas) {
    if (!status) return GENERIC_IS_INVALID;
    if (!icd10Coding) return GENERIC_IS_INVALID;
    if (!icd10Coding.code) return GENERIC_IS_INVALID;
    if (!icd10Coding.display) return GENERIC_IS_INVALID;
  } else if (currentEhr.isAthena) {
    if (!snomedCoding) return GENERIC_IS_INVALID;
    if (!startDate) return GENERIC_IS_INVALID;
  } else if (currentEhr.isPracticeFusion) {
    const hasValidIcd10 =
      !!icd10Coding?.code && !!icd10Coding?.display && icd10Coding.display !== UNKNOWN_DISPLAY;
    const hasValidSnomed =
      !!snomedCoding?.code && !!snomedCoding?.display && snomedCoding.display !== UNKNOWN_DISPLAY;
    if (!hasValidIcd10 && !hasValidSnomed) return GENERIC_IS_INVALID;
  }

  return IS_VALID;
}

// ============================================================================
// IMMUNIZATION VALIDATION
// ============================================================================

function validateImmunization(data: unknown, currentEhr: CurrentEhr): ValidationResult {
  const immunization = extractImmunization(data);
  if (!immunization) return GENERIC_IS_INVALID;

  // V1 immunization validation logic
  const cvxCoding = getFirstCodeSpecified(immunization.vaccineCode?.coding ?? [], [CVX_CODE]);
  if (!cvxCoding) return GENERIC_IS_INVALID;

  const date = immunization.occurrenceDateTime || immunization.occurrenceString;
  if (!date) return GENERIC_IS_INVALID;

  if (currentEhr.isCanvas) {
    if (!cvxCoding.code) return GENERIC_IS_INVALID;
    const status = immunization.status;
    if (!status) return GENERIC_IS_INVALID;
  }

  return IS_VALID;
}

// ============================================================================
// MEDICATION VALIDATION
// ============================================================================

function validateMedication(data: unknown, currentEhr: CurrentEhr): ValidationResult {
  // Extract MedicationWithRefs from V2 data structures
  let medicationWithRefs: MedicationWithRefs | undefined;

  // Check if it's EnhancedMedicationSeries
  if (typeof data === "object" && data !== null && "medicationWithRefs" in data && "code" in data) {
    medicationWithRefs = data.medicationWithRefs as MedicationWithRefs;
  } else if (isMedicationSeries(data)) {
    // Get most recent from series
    medicationWithRefs = data.resources[data.resources.length - 1];
  } else {
    // Might be raw MedicationWithRefs
    medicationWithRefs = data as MedicationWithRefs;
  }

  if (!medicationWithRefs) return GENERIC_IS_INVALID;

  const medication = medicationWithRefs.medication;

  // Healthie: Only requires medication name (from text or rxnorm display)
  if (currentEhr.isHealthie) {
    const rxNormCoding = getFirstCodeSpecified(medication?.code?.coding ?? [], [RX_NORM_CODE]);
    const text = medication?.code?.text;
    const medicationName = text ?? rxNormCoding?.display;
    if (!medicationName) return GENERIC_IS_INVALID;
    return IS_VALID;
  }

  // PracticeFusion: Only requires Medication Requests
  if (currentEhr.isPracticeFusion) {
    if (!medicationWithRefs.requests || medicationWithRefs.requests.length < 1) {
      return GENERIC_IS_INVALID;
    }
    return IS_VALID;
  }

  // Other EHRs: More strict validation
  if (!medicationWithRefs.statement || medicationWithRefs.statement.length < 1) {
    return GENERIC_IS_INVALID;
  }

  const rxNormCoding = getFirstCodeSpecified(medication?.code?.coding ?? [], [RX_NORM_CODE]);
  if (!rxNormCoding) return GENERIC_IS_INVALID;

  if (currentEhr.isCanvas) {
    if (!rxNormCoding.display || rxNormCoding.display === UNKNOWN_DISPLAY) {
      return GENERIC_IS_INVALID;
    }
    const statusesAndDosagesExist = medicationWithRefs.statement.find(statement => {
      if (!statement.status) return false;
      const dosageText = statement.dosage?.[0]?.text;
      if (!dosageText) return false;
      return true;
    });
    if (!statusesAndDosagesExist) return GENERIC_IS_INVALID;
    return IS_VALID;
  }

  const startDates = medicationWithRefs.statement.flatMap(statement => {
    const startDate = statement.effectiveDateTime ?? statement.effectivePeriod?.start;
    if (!startDate) return [];
    return [startDate];
  });

  if (startDates.length < 1) return GENERIC_IS_INVALID;

  return IS_VALID;
}

// ============================================================================
// PROCEDURE VALIDATION
// ============================================================================

function validateProcedure(data: unknown): ValidationResult {
  const procedure = extractProcedure(data);
  if (!procedure) return GENERIC_IS_INVALID;

  // V1 procedure validation logic
  const cptCoding = getFirstCodeSpecified(procedure.code?.coding ?? [], [CPT_CODE]);
  if (!cptCoding) return GENERIC_IS_INVALID;

  const date = procedure.performedDateTime || procedure.performedPeriod?.start;
  if (!date) return GENERIC_IS_INVALID;

  return IS_VALID;
}

// ============================================================================
// ALLERGY VALIDATION
// ============================================================================

function validateAllergy(data: unknown, currentEhr: CurrentEhr): ValidationResult {
  const allergyIntolerance = extractAllergy(data);
  if (!allergyIntolerance) return GENERIC_IS_INVALID;

  // V1 allergy validation logic
  const reaction = allergyIntolerance.reaction;
  if (!reaction || reaction.length < 1) return GENERIC_IS_INVALID;

  const onset = allergyIntolerance.onsetDateTime ?? allergyIntolerance.onsetPeriod?.start;
  const codingsPairs: [CodeMap, CodeMap | undefined][] = reaction.flatMap(r => {
    const substanceRxnormCoding = getFirstCodeSpecified(r.substance?.coding ?? [], [RX_NORM_CODE]);
    if (!substanceRxnormCoding) return [];
    const manifestationSnomedCoding = getFirstCodeSpecified(
      r.manifestation?.flatMap(m => m.coding ?? []),
      [SNOMED_CODE]
    );
    if (currentEhr.isAthena && !manifestationSnomedCoding) return [];
    return [[substanceRxnormCoding, manifestationSnomedCoding]];
  });

  if (currentEhr.isHealthie) {
    const hasValidReaction = reaction.some(r => !!r.substance?.text);
    if (!hasValidReaction) return GENERIC_IS_INVALID;
  } else if (currentEhr.isPracticeFusion) {
    const codeableConcept = allergyIntolerance.code ?? allergyIntolerance.reaction?.[0]?.substance;
    if (!codeableConcept) return GENERIC_IS_INVALID;
    const hasValidCodeableConcept = createPracticeFusionCodeableConcept(codeableConcept);
    if (!hasValidCodeableConcept) return GENERIC_IS_INVALID;
  } else if (currentEhr.isAthena) {
    if (codingsPairs.length < 1) return GENERIC_IS_INVALID;
    if (!onset) return GENERIC_IS_INVALID;
  } else if (currentEhr.isCanvas) {
    if (codingsPairs.length < 1) return GENERIC_IS_INVALID;
    const clinicalStatus = allergyIntolerance.clinicalStatus;
    if (!clinicalStatus) return GENERIC_IS_INVALID;
    const verificationStatus = allergyIntolerance.verificationStatus;
    if (!verificationStatus) return GENERIC_IS_INVALID;
  }

  return IS_VALID;
}

// ============================================================================
// NOTE VALIDATION
// ============================================================================

function validateNote(data: unknown): ValidationResult {
  const report = extractDiagnosticReport(data);
  if (!report) return GENERIC_IS_INVALID;

  // Check if there's text content to write
  const hasTextContent =
    (report.presentedForm && report.presentedForm.length > 0) || report.conclusion;

  if (!hasTextContent) return GENERIC_IS_INVALID;

  return IS_VALID;
}

// ============================================================================
// FAMILY HISTORY VALIDATION
// ============================================================================

function validateFamilyHistory(data: unknown): ValidationResult {
  const familyHistory = extractFamilyHistory(data);
  if (!familyHistory) return GENERIC_IS_INVALID;

  // V1 family history validation logic
  // Check for relationship (who the family member is)
  const relationship = familyHistory.relationship;
  if (!relationship) return GENERIC_IS_INVALID;

  // Check for at least one condition with SNOMED code
  const hasValidCondition = (familyHistory.condition ?? []).some(condition => {
    const snomedCoding = getFirstCodeSpecified(condition.code?.coding ?? [], [SNOMED_CODE]);
    return !!snomedCoding;
  });

  if (!hasValidCondition) return GENERIC_IS_INVALID;

  return IS_VALID;
}

// ============================================================================
// EXTRACTION HELPERS - Handle both V2 Series and raw FHIR resources
// ============================================================================

function extractObservation(data: unknown): Observation | undefined {
  if (isObservationSeries(data)) return data.resources[data.resources.length - 1];
  if (isObservation(data as Smart<Observation>)) return data as Observation;
  return undefined;
}

function extractCondition(data: unknown): Condition | undefined {
  if (isConditionSeries(data)) return data.resources[data.resources.length - 1];
  if (isCondition(data as Smart<Condition>)) return data as Condition;
  return undefined;
}

function extractImmunization(data: unknown): Immunization | undefined {
  if (isImmunizationSeries(data)) return data.resources[data.resources.length - 1];
  if (isImmunization(data as Smart<Immunization>)) return data as Immunization;
  return undefined;
}

function extractProcedure(data: unknown): Procedure | undefined {
  if (isProcedureSeries(data)) return data.resources[data.resources.length - 1];
  if (isProcedure(data as Smart<Procedure>)) return data as Procedure;
  return undefined;
}

function extractAllergy(data: unknown): AllergyIntolerance | undefined {
  if (isAllergyIntolerance(data as Smart<AllergyIntolerance>)) return data as AllergyIntolerance;
  return undefined;
}

function extractDiagnosticReport(data: unknown): DiagnosticReport | undefined {
  if (isDiagnosticReport(data as Smart<DiagnosticReport>)) return data as DiagnosticReport;
  return undefined;
}

function extractFamilyHistory(data: unknown): FamilyMemberHistory | undefined {
  if (isFamilyMemberHistory(data as Smart<FamilyMemberHistory>)) return data as FamilyMemberHistory;
  return undefined;
}
